import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useData } from '../contexts/DataContext';
import { Award, User, TrendingUp, Star, ArrowLeft, Loader } from 'lucide-react';
import logger from '../utils/logger';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f3f0;
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  background: #614f48;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  transition: background 0.2s;
  
  &:hover {
    background: #4a3c37;
  }
  
  @media (max-width: 768px) {
    position: static;
    margin-bottom: 15px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #000000;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.95rem;
`;

const Branding = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
  color: #231F20;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const BrandingLogo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  position: relative;
  min-height: 200px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 12px;
`;

const LeaderboardItem = styled.div`
  background: ${props => {
    if (props.rank === 1) return '#faaf40';
    if (props.rank === 2) return '#a9a9a9';
    if (props.rank === 3) return '#c56a2e';
    return '#fff';
  }};
  border: 3px solid ${props => props.rank <= 3 ? '#ffffff' : 'transparent'};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  color: ${props => props.rank <= 3 ? '#231F20' : '#000000'};
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 12px;
  }
`;

const RankBadge = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  min-width: 40px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    min-width: 30px;
  }
`;

const Content = styled.div`
  flex: 1;
`;

const Username = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const Details = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.9rem;
  opacity: ${props => props.rank <= 3 ? '0.95' : '0.8'};
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    gap: 8px;
  }
`;

const Detail = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Stats = styled.div`
  text-align: center;
  min-width: 80px;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  opacity: ${props => props.rank <= 3 ? '0.9' : '0.7'};
  margin-top: 2px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #231F20;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.3;
`;

const Badge = styled.span`
  background: ${props => props.rank <= 3 ? 'rgba(0, 0, 0, 0.25)' : '#5752e8'};
  color: ${props => props.rank <= 3 ? '#fff' : '#fff'};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

function ToiletTower() {
  const navigate = useNavigate();
  const { restrooms } = useData();
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    logger.log('🏗️ [ToiletTower] Fetching leaderboard.');
    setLoading(true);
    try {
      const functions = getFunctions();
      const getLeaderboard = httpsCallable(functions, 'GetTPLeaderboard');
      
      const payload = {
        timePeriod: 'all',
        limit: 100,
        database_id_hash: localStorage.getItem('database_id_hash') || ''
      };
      
      logger.log('🏗️ [ToiletTower] Calling GetTPLeaderboard with payload:', payload);
      
      const result = await getLeaderboard(payload);

      if (result.data.success) {
        logger.log(`🏗️ [ToiletTower] Leaderboard fetched successfully. Count: ${result.data.leaderboard.length}`);
        const normalized = (result.data.leaderboard || []).map((user) => ({
          username: user.username,
          userId: user.userId,
          title: user.title || 'Newcomer',
          totalTP: Number(user.totalTP || 0),
          windowTP: Number(user.windowTP || 0),
          restroomsCreated: 0,
          reviewsGiven: 0,
          totalContributions: Number(user.windowTP || 0)
        }));

        setTopUsers(normalized);
      } else {
        logger.warn('🏗️ [ToiletTower] Result returned success: false', result.data);
      }
    } catch (error) {
      logger.error('🏗️ [ToiletTower] Error fetching Porcelain Pyramid leaderboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleUserClick = (user) => {
    const identifier = user.userId || user.username;
    if (!identifier) return;
    navigate(`/app/profile/${encodeURIComponent(identifier)}`);
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/app')}>
          <ArrowLeft size={18} />
          Back to Map
        </BackButton>
        <Branding>
          <BrandingLogo src="/app-icon.png" alt="The Crapper Mapper" />
          Crapper Mapper
        </Branding>
        <Title>
          <TrendingUp size={32} />
          Porcelain Pyramid
        </Title>
        <Subtitle>All-Time Top TP Leaders</Subtitle>
      </Header>

      <LeaderboardList>
        {loading && (
          <LoadingOverlay>
            <Loader className="animate-spin" size={48} color="#5752e8" />
          </LoadingOverlay>
        )}
        
        {topUsers.length === 0 && !loading ? (
          <EmptyState>
            <EmptyIcon>🏆</EmptyIcon>
            <h3>No Data Yet!</h3>
            <p>Start reviewing restrooms to climb the pyramid!</p>
          </EmptyState>
        ) : (
          topUsers.map((user, index) => {
            const rank = index + 1;
            return (
              <LeaderboardItem key={user.username} rank={rank} onClick={() => handleUserClick(user)}>
                <RankBadge>
                  {rank === 1 && '🥇'}
                  {rank === 2 && '🥈'}
                  {rank === 3 && '🥉'}
                  {rank > 3 && `#${rank}`}
                </RankBadge>
                
                <Content>
                  <Username>
                    <User size={18} />
                    {user.username}
                    {rank === 1 && <Badge rank={rank}>Legend</Badge>}
                    {rank === 2 && <Badge rank={rank}>Elite</Badge>}
                    {rank === 3 && <Badge rank={rank}>Pro</Badge>}
                  </Username>
                  <Details rank={rank}>
                    <Detail>
                      <span>👑 {user.title}</span>
                    </Detail>
                    {/* <Detail>
                      <Star size={14} fill="currentColor" />
                      <span>{user.totalTP} Total TP</span>
                    </Detail> */}
                  </Details>
                </Content>
                
                <Stats>
                  <StatValue>{user.windowTP}</StatValue>
                  <StatLabel rank={rank}>TP</StatLabel>
                </Stats>
              </LeaderboardItem>
            );
          })
        )}
      </LeaderboardList>
    </Container>
  );
}

export default ToiletTower;
